import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';
const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 100, 200, 500, 1000, 2000];

export default function ReviewsTab({ t }) {
  const formatDate = (date) => { if (!date) return ''; const dateOnly = String(date).substring(0, 10); return new Date(dateOnly + 'T12:00:00').toLocaleDateString(); };

  // Data state
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Search state
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const searchTimer = useRef(null);

  // Filter state
  const [ratingFilter, setRatingFilter] = useState('');

  const pg = t.admin?.search || {};

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize, sort: 'date', order: 'desc' };
      if (searchDebounced) params.q = searchDebounced;
      if (ratingFilter) params.rating = ratingFilter;

      const res = await axios.get(`${API}/reviews/all`, { params });
      const data = res.data;
      setReviews(data.data || []);
      if (data.pagination) {
        setTotalPages(data.pagination.totalPages || 1);
        setTotal(data.pagination.total || 0);
      }
    } catch (err) {
      console.error('Failed to load reviews:', err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchDebounced, ratingFilter]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchDebounced(val);
      setPage(1);
    }, 300);
  };

  const handleRatingFilter = (val) => {
    setRatingFilter(val);
    setPage(1);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  // Pagination display
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div>
      <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', marginBottom: '2rem' }}>
        {t.admin.customerReviews} ({total} {t.admin.total})
      </h2>

      {/* Search bar + rating filter */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: '400px' }}>
          <input
            type="text"
            className="form-control"
            placeholder={pg.placeholder || 'Search by name, email, service\u2026'}
            value={search}
            onChange={e => handleSearch(e.target.value)}
            style={{ paddingRight: '2.5rem' }}
          />
          {search && (
            <button
              onClick={() => { setSearch(''); setSearchDebounced(''); setPage(1); }}
              aria-label={t.common?.close || 'Clear'}
              style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94A3B8', fontSize: '1.1rem', cursor: 'pointer', padding: '0.25rem' }}
            >&times;</button>
          )}
        </div>
        <select
          className="form-select"
          value={ratingFilter}
          onChange={e => handleRatingFilter(e.target.value)}
          style={{ width: 'auto', minWidth: '130px' }}
        >
          <option value="">{t.admin?.allRatings || 'All ratings'}</option>
          {[5, 4, 3, 2, 1].map(n => (
            <option key={n} value={n}>{'★'.repeat(n)} ({n})</option>
          ))}
        </select>
        {!loading && total > 0 && (
          <span style={{ fontSize: '0.85rem', color: '#94A3B8', marginLeft: 'auto' }}>
            {(pg.showing || '{from}\u2013{to} of {total}').replace('{from}', from).replace('{to}', to).replace('{total}', total)}
          </span>
        )}
      </div>

      {/* Review cards */}
      <div style={{ position: 'relative', minHeight: '200px' }}>
        {loading && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(12,18,32,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
            <span style={{ color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif" }}>{t.common?.loading || 'Loading...'}</span>
          </div>
        )}
        <div style={{ display: 'grid', gap: '1rem' }}>
          {reviews.map(review => (
            <div key={review.id} style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <div style={{ color: '#E8ECF0', fontSize: '1.1rem', marginBottom: '0.3rem' }}>
                    {review.first_name} {review.last_name}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#94A3B8' }}>
                    {Array.isArray(review.services) && review.services.length > 0
                      ? review.services.map(s => s.name_fr || s.name).join(' + ')
                      : (review.service_name_fr || review.service_name)} {'\u2022'} {formatDate(review.appointment_date)}
                  </div>
                </div>
                <div style={{ color: '#D42B2B', fontSize: '1.5rem' }}>
                  {'\u2B50'.repeat(review.rating)}
                </div>
              </div>
              {review.comment && (
                <div style={{ color: '#64748B', fontSize: '0.95rem', lineHeight: 1.6, fontStyle: 'italic', paddingLeft: '1rem', borderLeft: '2px solid rgba(148,163,184,0.3)' }}>
                  &ldquo;{review.comment}&rdquo;
                </div>
              )}
              <div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.8rem' }}>
                {review.email}
              </div>
            </div>
          ))}
          {!loading && reviews.length === 0 && (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#94A3B8' }}>
              {pg.noResults || t.admin.noReviews || 'No reviews found'}
            </div>
          )}
        </div>
      </div>

      {/* Pagination controls */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        {totalPages > 1 && (
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            {pg.prev || '\u2039 Previous'}
          </button>
        )}
        {totalPages > 1 && (
          <span style={{ color: '#94A3B8', fontSize: '0.9rem' }}>
            Page {page} / {totalPages}
          </span>
        )}
        {totalPages > 1 && (
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            {pg.next || 'Next \u203A'}
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <label style={{ color: '#94A3B8', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{pg.rowsPerPage || 'Rows:'}</label>
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={e => handlePageSizeChange(Number(e.target.value))}
            style={{ width: 'auto', minWidth: '70px' }}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
